import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Button, IconButton, Tooltip } from '@material-ui/core';
import useIsMobile from '../../../../hooks/useIsMobile';

const useStyles = makeStyles(theme => ({
    linha: {
        display: 'flex'
    },
    linhaMobile: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5)
    },
    /* Sem `cor` na ação, segue o azul claro dos ícones do projeto. */
    corPadrao: {
        color: theme.palette.tertiary.main
    }
}));

/*
 * Ações de uma linha da grade. Na tabela do desktop são ícones com tooltip;
 * no card do mobile viram botões com rótulo, porque no celular não há hover
 * para revelar o tooltip e "Remover" é destrutivo demais para ficar sem texto.
 */
export default function AcoesLinha({ acoes = [] }) {
    const classes = useStyles();
    const isMobile = useIsMobile();

    if (!acoes.length) {
        return null;
    }

    if (isMobile) {
        return (
            <div className={classes.linhaMobile}>
                {acoes.map(acao => (
                    <Button
                        key={acao.titulo}
                        size="small"
                        color={acao.cor}
                        className={acao.cor ? undefined : classes.corPadrao}
                        startIcon={acao.icone}
                        disabled={acao.desabilitado}
                        onClick={acao.onClick}>
                        {acao.titulo}
                    </Button>
                ))}
            </div>
        );
    }

    return (
        <div className={classes.linha}>
            {acoes.map(acao => (
                <Tooltip key={acao.titulo} title={acao.titulo} arrow>
                    <span>
                        <IconButton
                            size="small"
                            color={acao.cor}
                            className={acao.cor ? undefined : classes.corPadrao}
                            disabled={acao.desabilitado}
                            onClick={acao.onClick}>
                            {acao.icone}
                        </IconButton>
                    </span>
                </Tooltip>
            ))}
        </div>
    );
}

AcoesLinha.propTypes = {
    acoes: PropTypes.arrayOf(PropTypes.shape({
        titulo: PropTypes.string.isRequired,
        icone: PropTypes.node,
        onClick: PropTypes.func,
        cor: PropTypes.string,
        desabilitado: PropTypes.bool,
        visivel: PropTypes.bool
    }))
};

/*
 * Monta a coluna "Ações" no formato que a tabela e o card entendem.
 * `cardAction` é o que faz a coluna virar botão no rodapé do card no mobile —
 * sem ele o DataCardList descarta a coluna e as ações somem no celular.
 *
 * `obterAcoes(row)` devolve a lista da linha; ações com `visivel: false` são
 * removidas e, quando não sobra nenhuma, a coluna devolve `null` — assim o card
 * só ganha rodapé de ações onde a tabela também as mostra.
 */
export const criarColunaAcoes = (obterAcoes, { label = 'Ações', className } = {}) => ({
    label,
    filterable: false,
    sortable: false,
    cardAction: true,
    className,
    valueGetter: (valor, row) => {
        const acoes = (obterAcoes(row) || []).filter(acao => acao && acao.visivel !== false);
        return acoes.length ? <AcoesLinha acoes={acoes} /> : null;
    }
});
